import { createLogger } from '@esengine/ecs-framework';
import { NodeType, BlackboardValueType } from '../Types/TaskStatus';
const logger = createLogger('EditorFormatConverter');
/**
 * 编辑器格式转换器
 *
 * 将编辑器格式转换为运行时资产格式
 */
export class EditorFormatConverter {
    /**
     * 转换编辑器格式为资产格式
     *
     * @param editorData 编辑器数据
     * @param metadata 可选的元数据覆盖
     * @returns 行为树资产
     */
    static toAsset(editorData, metadata) {
        logger.info('开始转换编辑器格式到资产格式');
        const rootNode = this.findRootNode(editorData.nodes);
        if (!rootNode) {
            throw new Error('未找到根节点');
        }
        const assetMetadata = {
            name: metadata?.name || editorData.metadata?.name || 'Untitled Behavior Tree',
            version: metadata?.version || editorData.version || '1.0.0'
        };
        const description = metadata?.description || editorData.metadata?.description;
        if (description) {
            assetMetadata.description = description;
        }
        const createdAt = metadata?.createdAt || editorData.metadata?.createdAt;
        if (createdAt) {
            assetMetadata.createdAt = createdAt;
        }
        const modifiedAt = metadata?.modifiedAt || new Date().toISOString();
        if (modifiedAt) {
            assetMetadata.modifiedAt = modifiedAt;
        }
        const nodes = this.convertNodes(editorData.nodes);
        const blackboard = this.convertBlackboard(editorData.blackboard);
        const propertyBindings = this.convertPropertyBindings(editorData.connections, editorData.nodes, blackboard);
        const asset = {
            version: '1.0.0',
            metadata: assetMetadata,
            rootNodeId: rootNode.id,
            nodes,
            blackboard
        };
        if (propertyBindings.length > 0) {
            asset.propertyBindings = propertyBindings;
        }
        logger.info(`转换完成: ${nodes.length}个节点, ${blackboard.length}个黑板变量, ${propertyBindings.length}个属性绑定`);
        return asset;
    }
    /**
     * 查找根节点
     */
    static findRootNode(nodes) {
        return nodes.find(node => node.template.category === '根节点' ||
            node.data.nodeType === 'root') || null;
    }
    /**
     * 转换节点列表
     */
    static convertNodes(editorNodes) {
        return editorNodes.map(node => this.convertNode(node));
    }
    /**
     * 转换单个节点
     */
    static convertNode(editorNode) {
        const data = { ...editorNode.data };
        delete data.nodeType;
        if (editorNode.template.className) {
            data.className = editorNode.template.className;
        }
        return {
            id: editorNode.id,
            name: editorNode.template.displayName || editorNode.data.name || 'Node',
            nodeType: editorNode.template.type,
            data,
            children: editorNode.children || []
        };
    }
    /**
     * 转换黑板变量
     */
    static convertBlackboard(blackboard) {
        const variables = [];
        for (const [name, value] of Object.entries(blackboard)) {
            const type = this.inferBlackboardType(value);
            variables.push({
                name,
                type,
                defaultValue: value
            });
        }
        return variables;
    }
    /**
     * 推断黑板变量类型
     */
    static inferBlackboardType(value) {
        if (typeof value === 'number') {
            return BlackboardValueType.Number;
        }
        else if (typeof value === 'string') {
            return BlackboardValueType.String;
        }
        else if (typeof value === 'boolean') {
            return BlackboardValueType.Boolean;
        }
        else {
            return BlackboardValueType.Object;
        }
    }
    /**
     * 转换属性绑定
     */
    static convertPropertyBindings(connections, nodes, blackboard) {
        const bindings = [];
        const blackboardVarNames = new Set(blackboard.map(v => v.name));
        const propertyConnections = connections.filter(conn => conn.connectionType === 'property');
        for (const conn of propertyConnections) {
            const fromNode = nodes.find(n => n.id === conn.from);
            const toNode = nodes.find(n => n.id === conn.to);
            if (!fromNode || !toNode || !conn.toProperty) {
                logger.warn(`跳过无效的属性连接: from=${conn.from}, to=${conn.to}`);
                continue;
            }
            let variableName;
            if (fromNode.data.nodeType === 'blackboard-variable') {
                variableName = fromNode.data.variableName;
            }
            else if (conn.fromProperty) {
                variableName = conn.fromProperty;
            }
            if (!variableName) {
                logger.warn(`无法确定变量名: from节点=${fromNode.template.displayName}`);
                continue;
            }
            if (!blackboardVarNames.has(variableName)) {
                logger.warn(`属性绑定引用了不存在的黑板变量: ${variableName}`);
                continue;
            }
            bindings.push({
                nodeId: toNode.id,
                propertyName: conn.toProperty,
                variableName
            });
        }
        return bindings;
    }
    /**
     * 从资产格式转换回编辑器格式（用于加载）
     *
     * @param asset 行为树资产
     * @returns 编辑器格式数据
     */
    static fromAsset(asset) {
        logger.info('开始转换资产格式到编辑器格式');
        const nodes = this.convertNodesFromAsset(asset.nodes);
        const blackboard = {};
        for (const variable of asset.blackboard) {
            blackboard[variable.name] = variable.defaultValue;
        }
        const connections = this.convertPropertyBindingsToConnections(asset.propertyBindings || []);
        const nodeConnections = this.buildNodeConnections(asset.nodes);
        connections.push(...nodeConnections);
        const metadata = {
            name: asset.metadata.name
        };
        if (asset.metadata.description) {
            metadata.description = asset.metadata.description;
        }
        if (asset.metadata.createdAt) {
            metadata.createdAt = asset.metadata.createdAt;
        }
        if (asset.metadata.modifiedAt) {
            metadata.modifiedAt = asset.metadata.modifiedAt;
        }
        const editorData = {
            version: asset.metadata.version,
            metadata,
            nodes,
            connections,
            blackboard,
            canvasState: {
                offset: { x: 0, y: 0 },
                scale: 1
            }
        };
        logger.info(`转换完成: ${nodes.length}个节点, ${connections.length}个连接`);
        return editorData;
    }
    /**
     * 从资产格式转换节点
     */
    static convertNodesFromAsset(assetNodes) {
        return assetNodes.map((node, index) => {
            const position = {
                x: 100 + (index % 5) * 250,
                y: 100 + Math.floor(index / 5) * 150
            };
            const template = {
                displayName: node.name,
                category: this.inferCategory(node.nodeType),
                type: node.nodeType
            };
            if (node.data.className) {
                template.className = node.data.className;
            }
            return {
                id: node.id,
                template,
                data: { ...node.data },
                position,
                children: node.children
            };
        });
    }
    /**
     * 推断节点分类
     */
    static inferCategory(nodeType) {
        switch (nodeType) {
            case NodeType.Action:
                return '动作';
            case NodeType.Condition:
                return '条件';
            case NodeType.Composite:
                return '组合';
            case NodeType.Decorator:
                return '装饰器';
            default:
                return '其他';
        }
    }
    /**
     * 将属性绑定转换为连接
     */
    static convertPropertyBindingsToConnections(bindings) {
        const connections = [];
        for (const binding of bindings) {
            connections.push({
                from: 'blackboard',
                to: binding.nodeId,
                toProperty: binding.propertyName,
                connectionType: 'property'
            });
        }
        return connections;
    }
    /**
     * 根据children关系构建节点连接
     */
    static buildNodeConnections(nodes) {
        const connections = [];
        for (const node of nodes) {
            for (const childId of node.children) {
                connections.push({
                    from: node.id,
                    to: childId,
                    connectionType: 'node'
                });
            }
        }
        return connections;
    }
}
//# sourceMappingURL=EditorFormatConverter.js.map